import React, { useState } from 'react';

import { PHRSuccessCheckmark as SuccessCheckmark } from '@palmhr/palmhr-ui-lib';
import { useQueryClient } from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import Modal from 'react-modal';

import Menu from 'components/Menu/Menu';
import modalStyles from 'components/modalStyles';
import ClientContext from 'Customers/components/detailsModal/ClientContext';
import DetailsModal from 'Customers/components/detailsModal/DetailsModal';
import StatusChip from 'Customers/components/StatusChip/StatusChip';
import DetailsCustomer from 'Customers/CustomersPage/components/DetailsCustomer/DetailsCustomer';
import {
  useMutationSuspendCustomer,
  useMutationActivateCustomer,
  useMutationArchiveCustomer,
  useMutationRetryFailedCustomer,
  useMutationCancelTenantSubscription,
} from 'Customers/hooks/useCustomers';
import useSubscriptionSync from 'Customers/hooks/useSubscriptionSync';
import formatCurrency from 'utility/formatCurrency';

import AddAdminModal from '../../../CustomerAccounts/AddAdminModal/AddAdminModal';
import ExtendDemoModal from '../../../CustomerAccounts/ExtendDemoModal/ExtendDemoModal';
import { getAccountType, getAccountOptions } from '../../../utility';

const addModalStyle = cloneDeep(modalStyles);
addModalStyle.content.width = '640px';
addModalStyle.content.padding = '0';

const AccountsTableRow = ({
  accountType,
  subdomain,
  fullDomain,
  status,
  id,
  users,
  trialEnd,
  customer,
  subscriptionsCount,
  createdAt,
  currency,
  representative,
  email,
  mrr,
}) => {
  const queryClient = useQueryClient();
  const account = { accountType, subdomain, status, id };
  const [detailsModal, setDetailsModal] = useState(false);
  const [detailsCustomer, setDetailsCustomer] = useState(false);
  const [addAdminModal, setAddAdminModal] = useState(false);
  const [extendDemoModal, setExtendDemoModal] = useState(false);
  const [selected, setSelected] = useState(undefined);
  const [loadingAction, setLoadingAction] = useState(null);
  const [notification, setNotification] = useState(null);

  const { mutate: suspendCustomer } = useMutationSuspendCustomer();
  const { mutate: archiveCustomer } = useMutationArchiveCustomer();
  const { mutate: activateCustomer } = useMutationActivateCustomer();
  const { mutate: retryFailedCustomer } = useMutationRetryFailedCustomer();
  const { mutate: cancelSubscription } = useMutationCancelTenantSubscription();

  // Use the shared sync hook
  const syncHook = useSubscriptionSync(id, {
    onSuccess: () => {
      // Additional success handling if needed
    },
    onError: () => {
      // Additional error handling if needed
    },
  });

  const accountOptions = getAccountOptions(status, accountType);

  const openDetailsHandler = (item) => {
    setSelected(item);
  };

  const handleSuccess = async (message) => {
    await queryClient.invalidateQueries(['customersAccounts', id]);
    setNotification({
      show: true,
      success: true,
      message: message || 'Action completed successfully',
    });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  const handleError = (error, action) => {
    setNotification({
      show: true,
      success: false,
      message: error?.response?.data?.message || `Failed to ${action}`,
    });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  const handleAction = (action) => {
    if (loadingAction) return; // Prevent multiple actions

    switch (action) {
      case 'Suspend':
        setLoadingAction('Suspend');
        suspendCustomer(id, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Customer suspended successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'suspend customer');
          },
        });
        break;
      case 'SyncSubscription':
        syncHook.syncCustomerSubscription();
        break;
      case 'AddAdmin':
        setAddAdminModal(true);
        break;
      case 'ExtendDemo':
        setExtendDemoModal(true);
        break;
      case 'Activate':
        setLoadingAction('Activate');
        activateCustomer(id, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Customer activated successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'activate customer');
          },
        });
        break;
      case 'Retry':
        setLoadingAction('Retry');
        retryFailedCustomer(id, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Retry initiated successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'retry customer setup');
          },
        });
        break;
      case 'Archive':
        setLoadingAction('Archive');
        archiveCustomer(id, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Customer archived successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'archive customer');
          },
        });
        break;
      case 'Delete':
        setLoadingAction('Delete');
        cancelSubscription(subdomain, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Subscription cancelled successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'cancel subscription');
          },
        });
        break;
      default:
        break;
    }
  };
  const handleShowDetails = (e) => {
    e.stopPropagation();
    setSelected(account);

    const shouldShowDetails =
      status !== 'ACTIVE' &&
      status !== 'PENDING_SUSPENSION' &&
      status !== 'SUSPENDED' &&
      status !== 'CANCELLED' &&
      status !== 'ARCHIVED';
    if (status !== 'PENDING') {
      setDetailsCustomer(shouldShowDetails);
      setDetailsModal(!shouldShowDetails);
    }
  };

  return (
    <ClientContext.Provider value={{ selected, openDetailsHandler }}>
      <tr onClick={handleShowDetails}>
        <td className='d-flex align-items-center py-3 px-3'>{customer}</td>
        <td>
          <div className='d-inline'>
            <p>{representative || '--'}</p>
            <p className='text-gray-500 font-size-12'>{email}</p>
          </div>
        </td>
        <td>
          <span>
            {status === 'FAILED' ? (
              <i className='icon-not-verified-fill text-red me-2' />
            ) : (
              <i className='icon-verified1-fill text-green me-2' />
            )}
            {fullDomain}
          </span>
        </td>
        <td>{getAccountType.get(accountType)}</td>
        <td className='px-5'>{subscriptionsCount}</td>
        <td>{createdAt ? moment(createdAt).format('MMM DD, YYYY') : '--'}</td>
        <td>{`${currency || ''} ${formatCurrency(mrr)}`}</td>
        {(status === 'ACTIVE' ||
          status === 'PENDING_SUSPENSION' ||
          status === 'SUSPENDED' ||
          status === 'CANCELLED') && (
          <td>
            <StatusChip status={status} />
          </td>
        )}
        <td>
          {status !== 'PENDING' && accountOptions.length > 0 && (
            <div className='d-flex align-items-center'>
              <Menu
                data={accountOptions}
                onAction={(action) => handleAction(action)}
                isDisabled={!!loadingAction || syncHook.isLoading}
              />
              {(loadingAction || syncHook.isLoading) && (
                <span className='ms-2 text-muted small'>
                  <i className='icon-loading icon-spin me-1' />
                  {syncHook.isLoading
                    ? 'Syncing...'
                    : loadingAction === 'Archive'
                    ? 'Archiving...'
                    : loadingAction === 'Suspend'
                    ? 'Suspending...'
                    : loadingAction === 'Activate'
                    ? 'Activating...'
                    : loadingAction === 'Retry'
                    ? 'Retrying...'
                    : loadingAction === 'Delete'
                    ? 'Cancelling...'
                    : 'Processing...'}
                </span>
              )}
            </div>
          )}
        </td>
      </tr>
      <Modal
        isOpen={detailsModal}
        contentLabel='Add Client'
        ariaHideApp={false}
        closeTimeoutMS={300}
        style={addModalStyle}
        onRequestClose={() => {
          setDetailsModal(false);
        }}
      >
        <DetailsModal
          customer={selected || {}}
          onClose={() => {
            setDetailsModal(false);
          }}
        />
      </Modal>
      <Modal
        isOpen={detailsCustomer}
        contentLabel='Add Client'
        ariaHideApp={false}
        closeTimeoutMS={300}
        style={addModalStyle}
        onRequestClose={() => {
          setDetailsCustomer(false);
        }}
      >
        <DetailsCustomer
          customer={selected || {}}
          onClose={() => {
            setDetailsCustomer(false);
          }}
        />
      </Modal>
      <AddAdminModal
        isOpen={addAdminModal}
        id={id}
        users={users}
        onClose={() => {
          setAddAdminModal(false);
        }}
      />
      <ExtendDemoModal
        isOpen={extendDemoModal}
        trialEnd={trialEnd}
        id={id}
        onClose={() => {
          setExtendDemoModal(false);
        }}
      />
      {(notification?.show || syncHook.notification?.show) && (
        <SuccessCheckmark
          positionFixed
          text={syncHook.notification?.show ? syncHook.notification.message : notification.message}
          isFailed={syncHook.notification?.show ? !syncHook.notification.success : !notification.success}
        />
      )}
    </ClientContext.Provider>
  );
};
export default AccountsTableRow;
